import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Dna,
  Heart,
  Users,
  Globe,
  Microscope,
  Shield,
  Sparkles,
  TreeDeciduous,
  Stethoscope,
  Building2,
  PawPrint,
  TrendingUp,
  MessageSquare,
  Calendar,
  ChevronLeft,
  Lightbulb,
  Target,
  Rocket,
  ThumbsUp,
  ExternalLink
} from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const API = process.env.REACT_APP_BACKEND_URL;

const FuturePlansPage = () => {
  usePageTitle('Our Vision');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [votes, setVotes] = useState({});
  const [loading, setLoading] = useState(true);

  const currentFeatures = [
    { icon: TreeDeciduous, title: 'Family Trees & Ancestry', description: 'Visualize your pet\'s lineage and discover their heritage' },
    { icon: Stethoscope, title: 'Health Records', description: 'Secure, encrypted medical records accessible anywhere' },
    { icon: Heart, title: 'Matchmaking', description: 'Find compatible playmates and breeding matches' },
    { icon: Users, title: 'Communities', description: 'Topic-based groups for every interest' },
    { icon: MessageSquare, title: 'Forums & Discussions', description: 'Connect with fellow pet enthusiasts' },
    { icon: Calendar, title: 'Events & Meetups', description: 'Local gatherings, shows, and activities' },
  ];

  const nearTermPlans = [
    {
      id: 'dna-testing',
      icon: Dna,
      title: 'DNA Testing Integration',
      description: 'We\'re exploring partnerships with leading DNA testing providers to offer members discounted tests and seamless import of genetic results into pet profiles.',
      status: 'Exploring',
      color: 'bg-blue-100 text-blue-700'
    },
    {
      id: 'ngo-rescue',
      icon: Building2,
      title: 'NGO & Rescue Program',
      description: 'A dedicated program for verified rescue organizations and shelters to list adoptable pets and connect with potential adopters in our community.',
      status: 'In Development',
      color: 'bg-green-100 text-green-700'
    },
    {
      id: 'personal-calendar',
      icon: Calendar,
      title: 'Personal Pet Calendar',
      description: 'A unified calendar showing all your pet\'s appointments, events, medication reminders, and vet visits. Sync with Google Calendar to never miss an important date.',
      status: 'Planned',
      color: 'bg-purple-100 text-purple-700'
    },
    {
      id: 'event-archive',
      icon: Calendar,
      title: 'Event Archive & History',
      description: 'Browse past and future events in a calendar view. Preserve event memories and see what\'s coming up in your area.',
      status: 'Planned',
      color: 'bg-purple-100 text-purple-700'
    },
    {
      id: 'health-insights',
      icon: Shield,
      title: 'Enhanced Health Insights',
      description: 'Expanding our AI-powered health tools to provide more comprehensive wellness monitoring and early detection recommendations.',
      status: 'Planned',
      color: 'bg-purple-100 text-purple-700'
    },
    {
      id: 'international',
      icon: Globe,
      title: 'International Expansion',
      description: 'Growing our club network worldwide, with localized experiences and partnerships in new countries.',
      status: 'In Progress',
      color: 'bg-amber-100 text-amber-700'
    },
  ];

  const futureVision = [
    {
      id: 'research',
      icon: Microscope,
      title: 'Research Collaboration',
      description: 'Connecting pet owners with veterinary researchers for studies that advance animal health and welfare.'
    },
    {
      id: 'breed-insights',
      icon: TrendingUp,
      title: 'Breed Health Insights',
      description: 'Aggregated, anonymized health data to identify trends and provide breed-specific wellness recommendations.'
    },
    {
      id: 'ai-matching',
      icon: Sparkles,
      title: 'AI-Powered Matching',
      description: 'Smarter matchmaking that considers genetic compatibility, temperament, and health factors.'
    },
    {
      id: 'multi-species',
      icon: PawPrint,
      title: 'Multi-Species Support',
      description: 'Expanding to serve cat lovers, bird enthusiasts, and other pet communities.'
    },
  ];

  useEffect(() => {
    fetchVotes();
  }, []);

  const fetchVotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/api/wishlist/votes`, { headers });
      
      const voteMap = {};
      response.data.forEach(item => {
        voteMap[item.item_id] = {
          count: item.vote_count,
          voted: item.user_voted
        };
      });
      setVotes(voteMap);
    } catch (error) {
      console.error('Error fetching votes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (itemId) => {
    if (!user) {
      navigate('/login', { state: { from: '/about/future' } });
      return;
    }

    const currentVote = votes[itemId]?.voted || false;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/wishlist/vote`, {
        item_id: itemId,
        vote: !currentVote
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setVotes(prev => ({
        ...prev,
        [itemId]: {
          count: response.data.vote_count,
          voted: response.data.user_voted
        }
      }));

      if (response.data.user_voted) {
        toast.success('Vote recorded! Thanks for your feedback.');
      }
    } catch (error) {
      toast.error('Failed to record vote');
    }
  };

  const VoteButton = ({ itemId, size = 'default' }) => {
    const voteData = votes[itemId] || { count: 0, voted: false };
    const isSmall = size === 'small';
    
    return (
      <Button
        variant={voteData.voted ? 'default' : 'outline'}
        size={isSmall ? 'sm' : 'default'}
        onClick={(e) => {
          e.stopPropagation();
          handleVote(itemId);
        }}
        className={`${voteData.voted ? 'bg-primary' : ''} ${isSmall ? 'h-8' : ''}`}
        data-testid={`vote-${itemId}`}
      >
        <ThumbsUp className={`${isSmall ? 'w-3 h-3' : 'w-4 h-4'} ${voteData.voted ? '' : ''} mr-1`} />
        <span>{voteData.count}</span>
        {!isSmall && <span className="ml-1 hidden sm:inline">{voteData.voted ? 'Voted' : 'Want this'}</span>}
      </Button>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-16">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <Link 
            to="/about" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to About
          </Link>
          
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Rocket className="w-8 h-8 text-primary" />
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
            Our Vision for the Future
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-6">
            We're building more than a platform — we're creating a community dedicated to improving 
            the lives of pets and the people who love them.
          </p>
          
          {/* Forum Link */}
          <Link 
            to="/forums?category=future-plans"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            Join the discussion in our Future Plans forum
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-5xl">
        {/* Voting Info */}
        {!user && (
          <Card className="mb-8 bg-blue-50 border-blue-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ThumbsUp className="w-5 h-5 text-blue-600" />
                <span className="text-blue-800">Sign in to vote on features you'd like to see!</span>
              </div>
              <Link to="/login" state={{ from: '/about/future' }}>
                <Button size="sm">Sign In</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Origin Story */}
        <Card className="mb-12 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <Lightbulb className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold mb-2">Where It All Started</h2>
                <p className="text-muted-foreground">
                  Our vision began with a simple observation: while purebred dogs have extensive 
                  registries and documentation, the majority of beloved family pets — mixed breeds, 
                  rescues, and undocumented dogs — had nowhere to call home. We set out to create 
                  a universal platform that celebrates <em>all</em> dogs and their humans, regardless 
                  of pedigree or papers.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What We've Built */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <Target className="w-6 h-6 text-green-600" />
            <h2 className="text-2xl font-heading font-semibold">What We've Built So Far</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentFeatures.map((feature) => (
              <Card key={`current-${feature.title}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                      <feature.icon className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">{feature.title}</h3>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* What's Coming Next - with voting */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-heading font-semibold">What We're Working On</h2>
            </div>
            <span className="text-sm text-muted-foreground">Click to vote for features you want!</span>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {nearTermPlans.map((plan) => (
              <Card key={plan.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <plan.icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <CardTitle className="text-lg">{plan.title}</CardTitle>
                    </div>
                    <Badge className={plan.color}>{plan.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">{plan.description}</p>
                  <div className="flex justify-end">
                    <VoteButton itemId={plan.id} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Long-Term Vision - with voting */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Globe className="w-6 h-6 text-purple-600" />
              <h2 className="text-2xl font-heading font-semibold">The Bigger Picture</h2>
            </div>
          </div>
          <p className="text-muted-foreground mb-6">
            Beyond our immediate roadmap, we're dreaming big. Vote on the ideas that excite you most:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {futureVision.map((item) => (
              <div key={item.id} className="flex gap-4 p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-medium">{item.title}</h3>
                    <VoteButton itemId={item.id} size="small" />
                  </div>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <Card className="bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-heading font-semibold mb-3">Shape Our Future Together</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              The best ideas come from our community. Join the Future Plans forum to discuss 
              features, share detailed suggestions, and connect with others who share your vision.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/forums?category=future-plans"
                className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Discuss in Forum
              </Link>
              <Link 
                to="/beta-feedback" 
                className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                Submit Feedback
              </Link>
              <Link 
                to="/roadmap" 
                className="inline-flex items-center px-6 py-3 border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                View Roadmap
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Footer Note */}
        <p className="text-center text-sm text-muted-foreground mt-8">
          This page reflects our current thinking and aspirations. Plans may evolve as we learn 
          from our community and adapt to new opportunities. Your votes help us prioritize!
        </p>
      </div>
    </div>
  );
};

export default FuturePlansPage;
